import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const NotificationCenter = ({ userRole = 'client' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const channelRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    loadNotifications();

    // Realtime: new notification → prepend; updated notification → sync read state
    channelRef.current = supabase
      .channel(`notif-center-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        if (payload.new) {
          setNotifications(prev => [payload.new, ...prev]);
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        if (payload.new) {
          setNotifications(prev =>
            prev.map(n => n.id === payload.new.id ? { ...n, ...payload.new } : n)
          );
        }
      })
      .subscribe();

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [user?.id]);

  const loadNotifications = async () => {
    const { data, error } = await supabase
      ?.from('user_notifications')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false })
      ?.limit(50);

    if (!error && data) setNotifications(data);
  };

  const unreadCount = notifications?.filter((n) => !n?.read)?.length;

  const handleNotificationClick = async (notification) => {
    const notificationId = notification?.id;
    // Optimistic mark-read
    setNotifications((prev) =>
      prev?.map((n) => (n?.id === notificationId ? { ...n, read: true } : n))
    );
    await supabase
      ?.from('user_notifications')
      ?.update({ read: true })
      ?.eq('id', notificationId)
      ?.eq('user_id', user?.id);

    // Navigate to the relevant page based on notification type/title
    const contractTypes = ['contract_offer', 'contract_update', 'milestone_update', 'time_entry_update'];
    const isMessageNotif =
      notification?.type === 'new_message' ||
      notification?.title?.toLowerCase().includes('message');
    const isContractNotif = contractTypes.includes(notification?.type);

    if (isMessageNotif) {
      const senderId = notification?.metadata?.sender_id;
      const query = senderId ? `?userId=${senderId}` : '';
      const path =
        userRole === 'expert'
          ? `/expert-dashboard/messages${query}`
          : `/client-dashboard/messages${query}`;
      navigate(path);
      setIsOpen(false);
    } else if (isContractNotif) {
      const path =
        userRole === 'expert'
          ? '/expert-dashboard/contracts'
          : '/client-dashboard/activity';
      navigate(path);
      setIsOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    // Optimistic update
    setNotifications((prev) => prev?.map((n) => ({ ...n, read: true })));
    await supabase
      ?.from('user_notifications')
      ?.update({ read: true })
      ?.eq('user_id', user?.id)
      ?.eq('read', false);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins > 1 ? 's' : ''} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_message':      return 'MessageSquare';
      case 'contract_offer':   return 'FileText';
      case 'contract_update':  return 'FileCheck';
      case 'milestone_update': return 'CheckSquare';
      case 'time_entry_update':return 'Clock';
      case 'success':          return 'CheckCircle';
      case 'warning':          return 'AlertTriangle';
      case 'error':            return 'XCircle';
      default:                 return 'Info';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'new_message':      return 'text-primary';
      case 'contract_offer':   return 'text-primary';
      case 'contract_update':  return 'text-muted-foreground';
      case 'milestone_update': return 'text-warning';
      case 'time_entry_update':return 'text-info';
      case 'success':          return 'text-success';
      case 'warning':          return 'text-warning';
      case 'error':            return 'text-error';
      default:                 return 'text-primary';
    }
  };

  return (
    <div className="relative">
      <button
        className="relative p-2 rounded-lg hover:bg-muted transition-smooth"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <Icon name="Bell" size={24} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[90]"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-96 bg-card border border-border rounded-lg shadow-elevation-lg z-[100]">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-lg">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  className="caption text-primary hover:underline"
                  onClick={handleMarkAllRead}
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications?.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Icon name="Inbox" size={48} className="mx-auto mb-2" />
                  <p>No notifications</p>
                </div>
              ) : (
                notifications?.map((notification) => (
                  <div
                    key={notification?.id}
                    className={`p-4 border-b border-border cursor-pointer transition-smooth hover:bg-muted ${
                      !notification?.read ? 'bg-muted/50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      <div className={`flex-shrink-0 ${getNotificationColor(notification?.type)}`}>
                        <Icon name={getNotificationIcon(notification?.type)} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-sm">{notification?.title || 'Notification'}</h4>
                          {!notification?.read && (
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{notification?.message}</p>
                        <p className="caption text-muted-foreground mt-2">
                          {formatTimestamp(notification?.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {notifications?.length > 0 && (
              <div className="p-3 border-t border-border text-center">
                <button className="caption text-primary hover:underline">
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;
